import type { JSMol, RDKitModule } from '@rdkit/rdkit';
import { molecularWeight, pieceFormula, type FragmentFormula } from './chemicalValidation.ts';

/** #1110: one entry of the Drawing info panel -- a piece of the paper, or an ion pair. */
export interface MolecularPiece {
  /** As the top bar writes it: "C2H6", "2 CH4", or an ion pair's "C2H3O2- · Na+". */
  readonly formula: string;
  /** The piece's SMILES; an ion pair's ions joined with "."; null when SMILES can't express it (#1130). */
  readonly smiles: string | null;
  /** Of one piece ("2 CH4": one methane's); of the whole pair for an ion pair. */
  readonly molecularWeight: number;
  /** How many identical pieces this entry stands for; 1 for an ion pair. */
  readonly count: number;
  readonly ionPair: boolean;
  /** ADR 0026: a complex held by hapto bonds -- its SMILES is its pieces, which SMILES can't join. */
  readonly hapto?: boolean;
}

interface Fragment extends FragmentFormula {
  readonly smiles: string;
  readonly molecularWeight: number;
  count: number;
}

/**
 * #1110 (owner, 2026-10-29): what is on the paper, piece by piece, in the top bar's order (#1073):
 * largest first, identical pieces counted once. Ion pairs are grouped into one entry. First cut,
 * for the owner to judge once it works (docs/drawing-info-panel.md): the charged pieces form one
 * group when their charges balance, or when leaving out a single piece whose charge is the excess
 * makes them balance (that piece then stays on its own); otherwise nothing is grouped.
 */
export function molecularPieces(rdkit: RDKitModule, canonicalSmiles: string): readonly MolecularPiece[] {
  const fragments = new Map<string, Fragment>();
  for (const smiles of canonicalSmiles.split('.')) {
    const known = fragments.get(smiles);
    if (known) {
      known.count += 1;
      continue;
    }
    let molecule: JSMol | null = null;
    try {
      molecule = rdkit.get_mol(smiles);
      if (!molecule?.is_valid()) throw new Error(`RDKit could not read the piece ${smiles}.`);
      fragments.set(smiles, {
        ...pieceFormula(molecule),
        smiles,
        molecularWeight: molecularWeight(molecule),
        count: 1
      });
    } finally {
      molecule?.delete();
    }
  }
  const ordered = [...fragments.values()].sort(largestFirst);
  const pair = ionPair(ordered);
  const entries = ordered.flatMap((fragment) => {
    const left = fragment.count - (pair?.get(fragment) ?? 0);
    return left > 0 ? [{ ...fragment, count: left }] : [];
  });
  const pieces: (MolecularPiece & { readonly atoms: number })[] = entries.map((fragment) => ({
    formula: counted(fragment.formula, fragment.count),
    smiles: fragment.smiles,
    molecularWeight: fragment.molecularWeight,
    count: fragment.count,
    ionPair: false,
    atoms: fragment.atoms
  }));
  if (pair) {
    const members = [...pair].map(([fragment, count]) => ({ fragment, count }));
    pieces.push({
      formula: members.map(({ fragment, count }) => counted(fragment.formula, count)).join(' · '),
      smiles: members.flatMap(({ fragment, count }) => Array<string>(count).fill(fragment.smiles)).join('.'),
      molecularWeight: members.reduce((sum, { fragment, count }) => sum + fragment.molecularWeight * count, 0),
      count: 1,
      ionPair: true,
      atoms: members.reduce((sum, { fragment, count }) => sum + fragment.atoms * count, 0)
    });
  }
  return pieces.sort(largestFirst).map(({ atoms: _atoms, ...piece }) => piece);
}

function largestFirst(
  first: { readonly atoms: number; readonly formula: string },
  second: { readonly atoms: number; readonly formula: string }
): number {
  return second.atoms - first.atoms || first.formula.localeCompare(second.formula);
}

function counted(formula: string, count: number): string {
  return count === 1 ? formula : `${count} ${formula}`;
}

/** The ions grouped as one pair, each with how many of it the pair takes; null when none. */
function ionPair(fragments: readonly Fragment[]): Map<Fragment, number> | null {
  const ions = new Map(fragments.filter((fragment) => fragment.charge !== 0).map((ion) => [ion, ion.count]));
  const excess = [...ions].reduce((sum, [ion, count]) => sum + ion.charge * count, 0);
  if (excess !== 0) {
    const leftOver = [...ions.keys()].find((ion) => ion.charge === excess);
    if (!leftOver) return null;
    const count = ions.get(leftOver)! - 1;
    if (count === 0) ions.delete(leftOver);
    else ions.set(leftOver, count);
  }
  const units = [...ions.values()].reduce((sum, count) => sum + count, 0);
  return units >= 2 ? ions : null;
}
